import { defineMessages, useIntl } from 'react-intl';
import React, { createContext, useState, useCallback, useMemo, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { castPrice, castPeriod } from '@ct-react/core';
import { Link, NavLink } from 'react-router-dom';

var articleTypes = defineMessages({
    rentAccommodation: { id: "ctl-market-rent-accommodation", defaultMessage: "Location de courte durée", dataMatcher: "RentalAccommodationArticle" },
    seasonalAccommodation: { id: "ctl-market-seasonal-accommodation", defaultMessage: "Location saisonnière", dataMatcher: "SeasonalAccommodationArticle" },
    annualAccommodation: { id: "ctl-market-annual-accommodation", defaultMessage: "Location à l'année", dataMatcher: "AnnualAccommodationArticle" },
    saleAccommodation: { id: "ctl-market-sale-accommodation", defaultMessage: "Vente", dataMatcher: "SaleAccommodationArticle" }
});

var objectTypes = defineMessages({
    farmFacility: { id: "ctl-objects-farm-facility", defaultMessage: "Installation agricole", dataMatcher: 1 },
    mountainFarm: { id: "ctl-objects-mountain-farm", defaultMessage: "Ferme de montagne", dataMatcher: 2 },
    farm: { id: "ctl-objects-farm", defaultMessage: "Ferme", dataMatcher: 3 },
    apartment: { id: "ctl-objects-apartment", defaultMessage: "Appartement", dataMatcher: 4 },
    duplex: { id: "ctl-objects-duplex", defaultMessage: "Duplex", dataMatcher: 5 },
    atticApartment: { id: "ctl-objects-attic-apartment", defaultMessage: "Appartement en attique", dataMatcher: 6 },
    mansardApartment: { id: "ctl-objects-mansard-apartment", defaultMessage: "Appartement mansardé", dataMatcher: 7 },
    studio: { id: "ctl-objects-studio", defaultMessage: "Studio", dataMatcher: 8 },
    singleRoom: { id: "ctl-objects-single-room", defaultMessage: "Chambre individuelle", dataMatcher: 9 },
    furnishedApartment: { id: "ctl-objects-furnished-apartment", defaultMessage: "Appartement meublé", dataMatcher: 10 },
    groundFloorApartment: { id: "ctl-objects-ground-floor-apartment", defaultMessage: "Appartement de plain-pied", dataMatcher: 11 },
    bachelor: { id: "ctl-objects-bachelor", defaultMessage: "Garçonnière", dataMatcher: 12 },
    loft: { id: "ctl-objects-loft", defaultMessage: "Loft", dataMatcher: 13 },
    attic: { id: "ctl-objects-attic", defaultMessage: "Grenier", dataMatcher: 14 },
    collectiveGarden: { id: "ctl-objects-collective-garden", defaultMessage: "Jardin collectif", dataMatcher: 15 },
    hotel: { id: "ctl-objects-hotel", defaultMessage: "Hôtel", dataMatcher: 16 },
    restaurant: { id: "ctl-objects-restaurant", defaultMessage: "Restaurant", dataMatcher: 17 },
    coffeeBar: { id: "ctl-objects-coffee-bar", defaultMessage: "Café", dataMatcher: 18 },
    bar: { id: "ctl-objects-bar", defaultMessage: "Bistrot", dataMatcher: 19 },
    motel: { id: "ctl-objects-motel", defaultMessage: "Motel", dataMatcher: 31 },
    pub: { id: "ctl-objects-pub", defaultMessage: "Bistrot", dataMatcher: 32 },
    singleHouse: { id: "ctl-objects-single-house", defaultMessage: "Maison individuelle", dataMatcher: 33 },
    rowHouse: { id: "ctl-objects-row-house", defaultMessage: "Maison mitoyenne", dataMatcher: 34 },
    bifamiliarHouse: { id: "ctl-objects-bifamiliar-house", defaultMessage: "Maison bi-familiale", dataMatcher: 35 },
    groundFloorHouse: { id: "ctl-objects-ground-floor-house", defaultMessage: "Maison de plain-pied", dataMatcher: 36 },
    villa: { id: "ctl-objects-villa", defaultMessage: "Villa", dataMatcher: 37 },
    farmHouse: { id: "ctl-objects-farm-house", defaultMessage: "Maison de campagne", dataMatcher: 38 },
    chalet: { id: "ctl-objects-chalet", defaultMessage: "Chalet", dataMatcher: 43 },
    rusticHouse: { id: "ctl-objects-rustic-house", defaultMessage: "Maison rustique", dataMatcher: 44 },
    commercial: { id: "ctl-objects-commercial", defaultMessage: "Surface commerciale", dataMatcher: 48 },
    garage: { id: "ctl-objects-garage", defaultMessage: "Garage", dataMatcher: 54 },
    building: { id: "ctl-objects-building", defaultMessage: "Bâtiment", dataMatcher: 64 },
    parking: { id: "ctl-objects-parking", defaultMessage: "Place de parc", dataMatcher: 81 },
    plot: { id: "ctl-objects-plot", defaultMessage: "Terrain à bâtir", dataMatcher: 93 },
    farmPlot: { id: "ctl-objects-farm-plot", defaultMessage: "Terrain agricole", dataMatcher: 94 },
    commercialPlot: { id: "ctl-objects-commercial-plot", defaultMessage: "Terrain commercial", dataMatcher: 95 },
    industrialPlot: { id: "ctl-objects-industrial-plot", defaultMessage: "Terrain industrielle", dataMatcher: 96 },
    doubleRoom: { id: "ctl-objects-double-room", defaultMessage: "Chambre double", dataMatcher: 100 },
    accommodationGroup: { id: "ctl-objects-accommodation-group", defaultMessage: "Hébergement de groupe", dataMatcher: 101 },
    bedBreakfast: { id: "ctl-objects-bed-breakfast", defaultMessage: "Bed & Breakfast", dataMatcher: 102 }
});

var orientations = defineMessages({
    none: { id: "ctl-orientation-none", defaultMessage: "Indéfinie", dataMatcher: "NONE" },
    north: { id: "ctl-orientation-north", defaultMessage: "Nord", dataMatcher: "NORTH" },
    east: { id: "ctl-orientation-east", defaultMessage: "Est", dataMatcher: "EAST" },
    south: { id: "ctl-orientation-south", defaultMessage: "Sud", dataMatcher: "SOUTH" },
    west: { id: "ctl-orientation-west", defaultMessage: "Ouest", dataMatcher: "WEST" },
    northeast: { id: "ctl-orientation-northeast", defaultMessage: "Nord-est", dataMatcher: "NORTH_EAST" },
    southeast: { id: "ctl-orientation-southeast", defaultMessage: "Sud-est", dataMatcher: "SOUTH_EAST" },
    southwest: { id: "ctl-orientation-southwest", defaultMessage: "Sud-ouest", dataMatcher: "SOUTH_WEST" },
    northwest: { id: "ctl-orientation-northwest", defaultMessage: "Nord-ouest", dataMatcher: "NORTH_WEST" },
    panoramic: { id: "ctl-orientation-panoramic", defaultMessage: "Panoramique", dataMatcher: "PANORAMIC" }
});

var heatingSystems = defineMessages({
    fireplace: { id: "ctl-heating-system-fireplace", defaultMessage: "Cheminée ouverte", dataMatcher: "FIREPLACE" },
    insert: { id: "ctl-heating-system-insert", defaultMessage: "Insert / Foyer fermé", dataMatcher: "INSERT" },
    electric: { id: "ctl-heating-system-electric", defaultMessage: "Radiateurs électriques", dataMatcher: "ELECTRIC" },
    pellet: { id: "ctl-heating-system-pellet", defaultMessage: "Chaudière à pellets", dataMatcher: "PELLET_BOILER" },
    gas: { id: "ctl-heating-system-gas", defaultMessage: "Chaudière à gaz", dataMatcher: "GAS_BOILER" },
    oil: { id: "ctl-heating-system-oil", defaultMessage: "Chaudière à fioul / mazout", dataMatcher: "OIL_BOILER" },
    condensing: { id: "ctl-heating-system-condensing", defaultMessage: "Chaudière à condensation", dataMatcher: "CONDENSING_BOILER" },
    pump: { id: "ctl-heating-system-pump", defaultMessage: "Pompe à chaleur (PAC)", dataMatcher: "HEAT_PUMP" },
    hvac: { id: "ctl-heating-system-hvac", defaultMessage: "Système CVC (HVAC)", dataMatcher: "HVAC" },
    solar: { id: "ctl-heating-system-solr", defaultMessage: "Chauffe-eau solaire", dataMatcher: "SOLAR_WATER_HEATER" }
});

var transLibGroups = {
    objectTypes: objectTypes,
    articleTypes: articleTypes,
    orientations: orientations,
    heatingSystems: heatingSystems
};

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var defaultLocale = { code: "fr-CH", basename: "fr", label: "Français" };
var supportedLocales = __spreadArray(__spreadArray([], [defaultLocale], false), [
    { code: "de-CH", basename: "de", label: "Deutsch" },
    { code: "en-GB", basename: "en", label: "English" }
], false);
var defineLocale = function (acceptedLanguages, path, customLocales) {
    var localeRefs = customLocales !== null && customLocales !== void 0 ? customLocales : supportedLocales;
    var basenameMatcher = new RegExp("^/(".concat(localeRefs.map(function (l) { return l.basename; }).join("|"), ")/(.*)$"));
    var findLocale = function (basenames) {
        var prioritize = basenames
            .map(function (bn) { return localeRefs.find(function (l) { return l.basename === bn; }); })
            .filter(function (l) { return !!l; });
        return prioritize[0] || null;
    };
    var check = path.match(basenameMatcher);
    if (!!check) {
        var matchedUrlLocale = findLocale([check[1]]);
        if (!!matchedUrlLocale)
            return matchedUrlLocale;
    }
    var fallbackLocale = findLocale(acceptedLanguages.map(splitCountry));
    return fallbackLocale || defaultLocale;
};
var splitCountry = function (locale) { return locale.replace("_", "-").split("-")[0]; };

var LocaleContext = createContext({
    locale: defaultLocale,
    setLocale: function () { return void 0; }
});
var LocaleProvider = function (_a) {
    var initedLocale = _a.locale, _b = _a.beforeLocaleChange, beforeLocaleChange = _b === void 0 ? function () { return new Promise(function (r) { return r(void 0); }); } : _b, children = _a.children;
    var _c = useState(initedLocale), locale = _c[0], setLocale = _c[1];
    var changingLocale = useCallback(function (newLocale) {
        beforeLocaleChange(newLocale)
            .then(function () { return setLocale(newLocale); });
    }, []);
    var value = useMemo(function () { return ({ locale: locale, setLocale: changingLocale }); }, [locale]);
    return (React.createElement(LocaleContext.Provider, { value: value }, children));
};

var useLocaleContext = function () { return useContext(LocaleContext); };

var useLocaleUrl = function (withDomain) {
    var locale = useLocaleContext().locale;
    var location = useLocation();
    var cleanUrl = useCallback(function (path, customLocale) {
        var cleanDomain = !withDomain ? "" : (withDomain.slice(-1) === "/" ? withDomain.slice(0, -1) : withDomain);
        var cleanPath = path.charAt(0) === "/" ? path.substring(1) : path;
        return "".concat(cleanDomain, "/").concat((customLocale || locale).basename, "/").concat(cleanPath);
    }, [locale, withDomain]);
    var currentUrl = "".concat(!!withDomain ? withDomain : "").concat(location.pathname).concat(location.search).concat(location.hash);
    var buildLocaleHref = useCallback(function (to, customLocale) {
        if (typeof to === "object")
            return cleanUrl("".concat(to.pathname).concat(to.search).concat(to.hash), customLocale);
        return cleanUrl(to, customLocale);
    }, [locale]);
    return { currentUrl: currentUrl, buildLocaleHref: buildLocaleHref };
};
var useLocaleNavigate = function () {
    var locale = useLocaleContext().locale;
    var location = useLocation();
    var navigate = useNavigate();
    return useCallback(function (to, options) {
        if (typeof to === "number") {
            navigate(to);
            return;
        }
        var cleaning = function (path) {
            var cleanPath = path.charAt(0) === "/" ? path.substring(1) : path;
            return "/".concat(locale.basename, "/").concat(cleanPath);
        };
        if (typeof to === "object") {
            var newTo = __assign(__assign({}, to), (!!to.pathname) && { pathname: cleaning(to.pathname) });
            navigate(newTo, options !== null && options !== void 0 ? options : {});
            return;
        }
        navigate(cleaning(to), options !== null && options !== void 0 ? options : {});
    }, [locale, location]);
};

var commonTransDefs = defineMessages({
    textualPeriod: { id: "ctl-common-textual-period", defaultMessage: "du {from} au {to}" }
});

var price = function (intl, val, options) {
    if (options === void 0) { options = {}; }
    var _a = castPrice(val), amount = _a.amount, currency = _a.currency;
    return intl.formatNumber(amount, __assign(__assign({}, options), { style: "currency", currency: currency }));
};
var period = function (intl, val, withYear, textual) {
    if (withYear === void 0) { withYear = false; }
    var period = castPeriod(val);
    var startDate = period.start.toDate();
    var endDate = period.end.toDate();
    if (period.start.isSame(period.end, "month")) {
        var startDay_1 = intl.formatDate(startDate, { day: "numeric" });
        var endDay_1 = intl.formatDate(endDate, { day: "numeric" });
        var month = intl.formatDate(startDate, { month: "short" });
        var year = intl.formatDate(startDate, { year: "numeric" });
        return !textual
            ? "".concat(startDay_1, " - ").concat(endDay_1, " ").concat(month).concat(withYear ? " ".concat(year) : "")
            : intl.formatMessage(commonTransDefs.textualPeriod, {
                from: startDay_1,
                to: "".concat(endDay_1, " ").concat(month).concat(withYear ? " ".concat(year) : "")
            });
    }
    if (period.start.isSame(period.end, "year")) {
        var startDay_2 = intl.formatDate(startDate, { day: "2-digit", month: "short" });
        var endDay_2 = intl.formatDate(endDate, { day: "2-digit", month: "short" });
        var year = intl.formatDate(startDate, { year: "numeric" });
        return !textual
            ? "".concat(startDay_2, " - ").concat(endDay_2).concat(withYear ? " ".concat(year) : "")
            : intl.formatMessage(commonTransDefs.textualPeriod, {
                from: startDay_2,
                to: "".concat(endDay_2).concat(withYear ? " ".concat(year) : "")
            });
    }
    var startDay = intl.formatDate(startDate, __assign({ day: "2-digit", month: "short" }, (withYear) && { year: "numeric" }));
    var endDay = intl.formatDate(endDate, __assign({ day: "2-digit", month: "short" }, (withYear) && { year: "numeric" }));
    return !textual
        ? "".concat(startDay, " - ").concat(endDay)
        : intl.formatMessage(commonTransDefs.textualPeriod, { from: startDay, to: endDay });
};
var libTranslate = function (intl, group, matcher) {
    var _a;
    var value = (_a = Object.values(transLibGroups[group])) === null || _a === void 0 ? void 0 : _a.find(function (def) { return !!def.dataMatcher && def.dataMatcher === matcher; });
    return !!value ? intl.formatMessage(value) : "";
};
var useLocaleFormatter = function () {
    var intl = useIntl();
    return {
        transLib: {
            use: function (value) { return intl.formatMessage(value); },
            match: function (group, match) { return libTranslate(intl, group, match); }
        },
        print: {
            price: function (value, options) { return price(intl, value, options); },
            period: function (value, withYear, textual) {
                if (withYear === void 0) { withYear = false; }
                return period(intl, value, withYear, textual);
            },
            ellipsis: function (text, maxChar) {
                if (maxChar === void 0) { maxChar = 160; }
                return text.length <= maxChar ? text : "".concat(text.slice(0, maxChar - 3), "...");
            }
        }
    };
};

var LangLink = function (_a) {
    var destinedLocale = _a.locale, onPropsClick = _a.onClick, props = __rest(_a, ["locale", "onClick"]);
    var _b = useLocaleContext(), currentLocale = _b.locale, setLocale = _b.setLocale;
    var currentUrl = useLocaleUrl().currentUrl;
    var destination = useMemo(function () {
        var path = currentUrl.replace(new RegExp("^/".concat(currentLocale.basename, "/")), "");
        return "/".concat(destinedLocale.basename, "/").concat(path);
    }, [currentLocale, currentUrl]);
    var onClick = function (e) {
        setLocale(destinedLocale);
        !!onPropsClick && onPropsClick(e);
    };
    return (React.createElement(Link, __assign({ to: destination, replace: true, onClick: onClick }, props)));
};

var LocaleLink = function (_a) {
    var to = _a.to, props = __rest(_a, ["to"]);
    var buildLocaleHref = useLocaleUrl().buildLocaleHref;
    return React.createElement(Link, __assign({ to: buildLocaleHref(to) }, props));
};
var LocaleNavLink = function (_a) {
    var to = _a.to, props = __rest(_a, ["to"]);
    var buildLocaleHref = useLocaleUrl().buildLocaleHref;
    return React.createElement(NavLink, __assign({ to: buildLocaleHref(to) }, props));
};

export { LangLink, LocaleLink, LocaleNavLink, LocaleProvider, defaultLocale, defineLocale, supportedLocales, transLibGroups, useLocaleContext, useLocaleFormatter, useLocaleNavigate, useLocaleUrl };
